import { useMemo } from 'react';

import { ContainerSize } from './useContainerSize';

export type ResponsiveScaleOptions = {
  /** Width (px) at which the width contributes a scale of 1 — the design width. */
  referenceWidth: number;
  /**
   * Optional height (px) at which the height contributes a scale of 1. When set,
   * the scale is driven by whichever dimension is more constrained
   * (`min(width / referenceWidth, height / referenceHeight)`), so a short/wide
   * box (e.g. landscape on a phone) shrinks instead of overflowing.
   */
  referenceHeight?: number;
  /** Smallest scale allowed, so content stays legible when the box is tiny. */
  min?: number;
  /** Largest scale allowed — defaults to 1 so content never grows past its design size. */
  max?: number;
};

/**
 * Derive a single scale factor from a container's measured size.
 *
 * Pairs with {@link useContainerSize}: that hook measures, this one decides how
 * big the content should be. Apply the result as a `transform: scale()` (e.g. via
 * a CSS custom property) so children scale uniformly in one place.
 *
 * The factor is linear in the constraining dimension and clamped to `[min, max]`.
 * At or above the reference size it is `max` (1 by default), so full-size layouts
 * are untouched.
 */
export function useResponsiveScale(
  size: ContainerSize,
  { referenceWidth, referenceHeight, min = 0.5, max = 1 }: ResponsiveScaleOptions
): number {
  return useMemo(() => {
    // No measurement yet — assume full size to avoid a flash of shrunken content.
    if (!size.width || referenceWidth <= 0) return max;

    const widthRatio = size.width / referenceWidth;
    const heightRatio =
      referenceHeight && referenceHeight > 0 && size.height
        ? size.height / referenceHeight
        : Infinity;

    return Math.max(min, Math.min(max, widthRatio, heightRatio));
  }, [size.width, size.height, referenceWidth, referenceHeight, min, max]);
}